import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import {
  Carte,
  Chargement,
  Erreur,
  Etiquette,
  Montant,
  Nombre,
  Vide,
} from '../../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf, formaterFcfa } from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { MouvementsProduit } from './MouvementsProduit.tsx';
import type { LigneStock } from './types.ts';

/**
 * État du stock, par produit et par boutique — RG-048, RG-186.
 *
 * Une ligne par couple produit-boutique, jamais un total : consolider
 * cacherait qu'un article dort dans un dépôt pendant qu'il manque au
 * comptoir — précisément l'information qui déclenche un transfert.
 *
 * Le stock affiché vient d'un CACHE. Quand un chiffre surprend, « Historique »
 * ouvre le journal des mouvements, qui est la source (RG-040).
 */
export function Stock(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { boutiqueId } = useSession();
  const [recherche, setRecherche] = useState('');
  const [sousSeuil, setSousSeuil] = useState(false);
  const [ouvert, setOuvert] = useState<LigneStock | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['stock', boutiqueId, recherche, sousSeuil],
    queryFn: () =>
      appeler<{ donnees: LigneStock[] }>(
        '/stock?' +
          [
            boutiqueId === null ? '' : `boutique_id=${boutiqueId}`,
            recherche.trim() === '' ? '' : `q=${encodeURIComponent(recherche.trim())}`,
            sousSeuil ? 'sous_seuil=true' : '',
          ]
            .filter((morceau) => morceau !== '')
            .join('&'),
      ),
    select: (reponse) => reponse.donnees,
  });
  const avecValeur = (data ?? []).some((ligne) => ligne.valeur !== undefined);

  return (
    <>
      <Entete
        titre="Stock"
        sous="Par produit ET par boutique : un article peut manquer ici et dormir ailleurs."
      />
      <Erreur erreur={error} />

      <div className="pile">
        {ouvert !== null && (
          <MouvementsProduit
            ligne={ouvert}
            onFerme={() => {
              setOuvert(null);
            }}
          />
        )}

        <Carte>
          <div className="champs">
            <div className="champ">
              <label htmlFor="recherche-stock">Référence ou désignation</label>
              <input
                id="recherche-stock"
                placeholder="Rechercher…"
                value={recherche}
                onChange={(evenement) => {
                  setRecherche(evenement.target.value);
                }}
              />
            </div>
            <div className="champ">
              <label style={{ marginTop: 22 }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto', marginRight: 8 }}
                  checked={sousSeuil}
                  onChange={(evenement) => {
                    setSousSeuil(evenement.target.checked);
                  }}
                />
                À réapprovisionner seulement
              </label>
            </div>
          </div>

          {isPending ? (
            <Chargement />
          ) : data === undefined || data.length === 0 ? (
            <Vide
              texte={
                sousSeuil
                  ? 'Aucun produit sous son seuil.'
                  : 'Aucun stock. Enregistrez une réception.'
              }
            />
          ) : (
            <>
              <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Stock',
                        nomFichier: 'stock',
                        colonnes: [
                          { cle: 'reference', entete: 'Référence', rendu: () => null, texte: (l: LigneStock) => l.reference },
                          { cle: 'designation', entete: 'Produit', rendu: () => null, texte: (l: LigneStock) => l.designation },
                          { cle: 'boutique', entete: 'Boutique', rendu: () => null, texte: (l: LigneStock) => l.boutique },
                          {
                            cle: 'quantite',
                            entete: 'Stock',
                            numerique: true,
                            rendu: () => null,
                            texte: (l: LigneStock) => String(l.quantite),
                          },
                          {
                            cle: 'seuil',
                            entete: 'Seuil',
                            numerique: true,
                            rendu: () => null,
                            texte: (l: LigneStock) => (l.seuil === null ? '—' : String(l.seuil)),
                          },
                          ...(avecValeur
                            ? [
                                {
                                  cle: 'valeur',
                                  entete: 'Valeur',
                                  numerique: true,
                                  rendu: () => null,
                                  texte: (l: LigneStock) =>
                                    l.valeur === undefined ? '—' : formaterFcfa(l.valeur),
                                },
                              ]
                            : []),
                        ],
                        lignes: data,
                      }),
                    );
                  }}
                />
              </div>
              <div className="tableau-defilant">
              <table>
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Produit</th>
                    <th>Boutique</th>
                    <th>Stock</th>
                    <th>Seuil</th>
                    {avecValeur && <th>Valeur</th>}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.map((ligne) => (
                    <tr key={`${ligne.produit_id}-${ligne.boutique_id}`}>
                      <td className="mono">{ligne.reference}</td>
                      <td>{ligne.designation}</td>
                      <td className="mono">{ligne.boutique}</td>
                      <td>
                        {ligne.quantite <= 0 ? (
                          <Etiquette ton="rouge" texte={String(ligne.quantite)} />
                        ) : ligne.sous_seuil ? (
                          <Etiquette ton="ambre" texte={String(ligne.quantite)} />
                        ) : (
                          <Nombre valeur={ligne.quantite} />
                        )}
                      </td>
                      <td>
                        {ligne.seuil === null ? (
                          <span className="mono">—</span>
                        ) : (
                          <Nombre valeur={ligne.seuil} />
                        )}
                      </td>
                      {avecValeur && (
                        <td>
                          <Montant valeur={ligne.valeur} />
                        </td>
                      )}
                      <td>
                        <button
                          type="button"
                          className="bouton--discret"
                          onClick={() => {
                            setOuvert(ligne);
                          }}
                        >
                          Historique
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </Carte>
      </div>
    </>
  );
}
